// Lo que el agente ha sacado del archivo, en el mismo diálogo (4.6, D11).
//
// **La puerta va donde está la atención.** Un archivo subido nace excluido de
// la búsqueda y espera un sí (2.8); si ese sí hay que ir a buscarlo a otra
// pantalla, el archivo se queda sin usar y nadie sabe por qué. Aquí aparece
// mientras el cliente todavía está mirando lo que acaba de subir.
//
// **No se aprueba solo por subirlo.** Este componente enseña y deja decidir;
// quien no decide, no publica nada. Si se cierra el diálogo, lo propuesto sigue
// esperando arriba de la lista, que es la misma puerta de 4.3.
//
// **La espera es real y hay que contarla.** El análisis corre en Celery después
// de la subida, así que durante unos segundos no hay nada que enseñar. Un
// diálogo vacío sin explicación se lee como un error; por eso se dice que el
// agente está leyendo, y se pregunta cada poco hasta que aparece algo.

import { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Button } from '@/components/ui/button'
import { LoadingSpinner } from '@/components/shared/loading-spinner'
import { PendingGate } from './PendingGate'
import { workspacesService, type Pendiente } from '@/services/workspacesService'

/** Cada cuánto se vuelve a preguntar mientras el agente lee. */
const CADA = 2000
/** Cuánto se espera antes de dejar de preguntar. El análisis de un archivo
 *  largo pasa por tres preguntas al modelo; un minuto es holgado y evita que
 *  un diálogo olvidado abierto siga preguntando toda la tarde. */
const HASTA = 60000

export function UploadGate({
  documento,
  archivo,
  onCerrar,
}: {
  /** El identificador del documento recién subido. */
  documento: string
  /** Su nombre, para poder decir qué se está leyendo. */
  archivo: string
  onCerrar: () => void
}) {
  const { t } = useTranslation()
  const [suyas, setSuyas] = useState<Pendiente[]>([])
  const [leyendo, setLeyendo] = useState(true)
  const [aceptandoTodas, setAceptandoTodas] = useState(false)
  // Para que el intervalo no dependa de un estado que cambia en cada vuelta.
  const desde = useRef(Date.now())

  const mirar = useCallback(async () => {
    const todas = await workspacesService.pendientes()
    // Por identificador y no por el nombre del archivo: dos pueden llamarse
    // igual, y entonces se enseñarían las del otro.
    const mias = todas.filter((p) => p.documento === documento)
    setSuyas(mias)
    return mias
  }, [documento])

  useEffect(() => {
    // En cuanto hay respuesta se deja de preguntar, y el reloj no se vuelve a
    // montar: sin esto el intervalo seguiría vivo toda la sesión haciendo nada,
    // que es la forma silenciosa de dejarse un `setInterval` abierto.
    if (!leyendo) return
    let vivo = true
    const preguntar = async () => {
      const mias = await mirar()
      if (!vivo) return
      // Lo primero que aparece es siempre la referencia, así que si hay una hay
      // respuesta; lo de dentro llega en la misma tanda. Y el plazo corta por
      // si el análisis nunca llega: un diálogo que gira para siempre miente
      // sobre lo que está pasando.
      if (mias.length > 0 || Date.now() - desde.current > HASTA) setLeyendo(false)
    }
    void preguntar()
    const reloj = setInterval(() => void preguntar(), CADA)
    return () => {
      vivo = false
      clearInterval(reloj)
    }
  }, [mirar, leyendo])

  const aceptarTodas = async () => {
    setAceptandoTodas(true)
    try {
      // En serie y no en paralelo: aceptar la referencia levanta la exclusión
      // del archivo, y lanzar cinco a la vez contra la misma fila es pedir una
      // carrera para ahorrar medio segundo.
      for (const pendiente of suyas) {
        await workspacesService.resolverPendiente({
          que: pendiente.que,
          id: pendiente.id,
          decision: 'aprobar',
          por: t('knowledge.puerta.firma'),
          motivo: '',
        })
      }
      await mirar()
    } finally {
      setAceptandoTodas(false)
    }
  }

  if (leyendo) {
    return (
      <div className="space-y-2 py-4 text-center">
        <LoadingSpinner size="sm" />
        <p className="text-sm">{t('knowledge.subida.leyendo', { archivo })}</p>
        <p className="text-xs text-muted-foreground">
          {t('knowledge.subida.puedes_cerrar')}
        </p>
      </div>
    )
  }

  if (suyas.length === 0) {
    return (
      <div className="space-y-4 py-4">
        <p className="text-sm">{t('knowledge.subida.listo')}</p>
        <Button size="sm" onClick={onCerrar}>
          {t('knowledge.subida.cerrar')}
        </Button>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      <PendingGate pendientes={suyas} onResuelta={() => void mirar()} />

      {/* Sin borde: el escalon de separacion ya lo da el `space-y-4` de
          arriba, y el estandar dice que agrupar se hace subiendo un
          escalon y no metiendo una caja. */}
      <div className="flex flex-wrap items-center gap-2">
        {suyas.length > 1 && (
          <Button size="sm" disabled={aceptandoTodas} onClick={aceptarTodas}>
            {t('knowledge.subida.todas', { count: suyas.length })}
          </Button>
        )}
        <Button size="sm" variant="outline" onClick={onCerrar}>
          {t('knowledge.subida.cerrar')}
        </Button>
        <p className="text-xs text-muted-foreground">
          {t('knowledge.subida.esperan')}
        </p>
      </div>
    </div>
  )
}
